import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ResizablePanel.module.css';

const MIN_HEIGHT_PX = 250;
const DEFAULT_HEIGHT_PERCENT = 50;

type Props = {
  children: React.ReactNode;
  className?: string;
  /** Доля высоты родителя для начальной высоты (0–100). По умолчанию 50. */
  defaultHeightPercent?: number;
  /** Минимальная высота в px. По умолчанию 250. */
  minHeightPx?: number;
  container: HTMLElement;
};

export function ResizablePanel({
  children,
  className,
  defaultHeightPercent = DEFAULT_HEIGHT_PERCENT,
  minHeightPx = MIN_HEIGHT_PX,
  container,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);
  const [heightPx, setHeightPx] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ y: number; height: number } | null>(null);

  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el;
      if (el?.parentElement && !initializedRef.current) {
        initializedRef.current = true;
        const parentHeight = el.parentElement.clientHeight;
        const initial = Math.max(minHeightPx, Math.round((parentHeight * defaultHeightPercent) / 100));
        setHeightPx(initial);
      }
    },
    [defaultHeightPercent, minHeightPx],
  );

  useEffect(() => {
    if (!container) return;
    const ro = new ResizeObserver(() => {
      if (!initializedRef.current) return;
      const parentHeight = container.clientHeight;
      setHeightPx((prev) => {
        if (prev === null) return null;
        return Math.min(prev, Math.max(minHeightPx, parentHeight));
      });
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [minHeightPx, container]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const currentHeight = heightPx ?? minHeightPx;
      dragStartRef.current = { y: e.clientY, height: currentHeight };
      setIsDragging(true);
    },
    [heightPx, minHeightPx],
  );

  useEffect(() => {
    if (!isDragging) return;

    const maxHeight = container ? container.clientHeight : Infinity;

    const handleMouseMove = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      const deltaY = e.clientY - start.y;
      const newHeight = Math.min(maxHeight, Math.max(minHeightPx, start.height - deltaY));
      setHeightPx(newHeight);
    };

    const handleMouseUp = () => {
      dragStartRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [container, isDragging, minHeightPx]);

  const height = heightPx ?? minHeightPx;

  return (
    <div
      ref={setContainerRef}
      className={clsx(styles.panel, isDragging && styles.panelDragging, className)}
      style={{ height: `${height}px`, minHeight: minHeightPx }}
    >
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-valuenow={height}
        className={styles.resizeHandle}
        onMouseDown={handleResizeStart}
      />
      {children}
    </div>
  );
}
